import React, {Dispatch, FC, SetStateAction, useCallback} from 'react';
import {Button, Form} from 'react-bootstrap';
import {EnvSetup} from 'enums/Envs';
import {RootRoutes} from 'enums/RootRoutes';
import {
    AbuseRulePreviewDisplayRow,
    AbuseWord,
    abuseWordsService,
    formatPreviewRowGender,
    normalizeAbuseRulePreview,
} from 'services/abuseWordsService';

export type AbuseWordPreviewDisplay = ReturnType<typeof normalizeAbuseRulePreview>;

const PreviewRow: FC<{ row: AbuseRulePreviewDisplayRow }> = ({row}) => {
    if (row.type === 'text') {
        return <span className="text-break">{row.text}</span>;
    }
    const genderText = formatPreviewRowGender(row.gender);
    const href = `${RootRoutes.Profile}/${encodeURIComponent(row.profileKey)}`;
    return (
        <span className="text-break">
            {row.numericId != null ? (
                <>
                    {row.userKey ? (
                        <>
                            <span className="text-muted">{row.userKey}</span>
                            {' '}·{' '}
                        </>
                    ) : null}
                    <a href={href} target="_blank" rel="noopener noreferrer">
                        #{row.numericId}
                    </a>
                </>
            ) : (
                <a href={href} target="_blank" rel="noopener noreferrer" className="text-break">
                    {row.profileKey}
                </a>
            )}
            {row.name ? (
                <>
                    {' '}
                    · <span>{row.name}</span>
                </>
            ) : null}
            {genderText ? (
                <>
                    {' '}
                    <span className="text-muted">— {genderText}</span>
                </>
            ) : null}
        </span>
    );
};

type Props = {
    word: AbuseWord;
    env: EnvSetup;
    previewLimit: number;
    setPreviewLimit: Dispatch<SetStateAction<number>>;
    previewLoading: boolean;
    setPreviewLoading: Dispatch<SetStateAction<boolean>>;
    previewDisplay: AbuseWordPreviewDisplay | null;
    setPreviewDisplay: Dispatch<SetStateAction<AbuseWordPreviewDisplay | null>>;
};

export const AbuseWordModalPreviewSection: FC<Props> = ({
                                                            word,
                                                            env,
                                                            previewLimit,
                                                            setPreviewLimit,
                                                            previewLoading,
                                                            setPreviewLoading,
                                                            previewDisplay,
                                                            setPreviewDisplay,
                                                        }) => {
    const handlePreview = useCallback(async () => {
        if (!word.id) {
            return;
        }
        setPreviewLoading(true);
        setPreviewDisplay(null);
        try {
            const raw = await abuseWordsService.preview(word.id, previewLimit, env);
            if (raw !== undefined) {
                setPreviewDisplay(normalizeAbuseRulePreview(raw));
            }
        } finally {
            setPreviewLoading(false);
        }
    }, [word.id, previewLimit, env, setPreviewLoading, setPreviewDisplay]);

    return (
        <div className="border rounded-3 p-3 h-100 d-flex flex-column bg-white shadow-sm">
            <Form.Label className="fw-medium">Превью по базе</Form.Label>
            <Form.Text muted className="d-block mb-3">
                Список пользователей из API превью; действия к профилям не применяются.
            </Form.Text>
            {word.id > 0 ? (
                <>
                    <div className="d-flex flex-row flex-wrap align-items-end gap-2 mb-3">
                        <div style={{width: '7rem'}}>
                            <Form.Label className="small text-muted">Лимит (1-1000)</Form.Label>
                            <Form.Control
                                type="number"
                                min={1}
                                max={1000}
                                size="sm"
                                value={previewLimit}
                                onChange={e => {
                                    const v = parseInt(e.target.value, 10);
                                    setPreviewLimit(Number.isFinite(v) ? v : 100);
                                }}
                            />
                        </div>
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={previewLoading}
                            onClick={() => void handlePreview()}
                        >
                            {previewLoading ? 'Загрузка…' : 'Показать превью'}
                        </Button>
                    </div>
                    <div
                        className="border rounded-2 bg-light flex-grow-1 d-flex flex-column"
                        style={{minHeight: 'min(28vh, 240px)', maxHeight: 'min(40vh, 360px)'}}
                    >
                        <div className="small text-muted px-2 py-1 border-bottom bg-white rounded-top">
                            Результат
                        </div>
                        <div className="flex-grow-1 overflow-auto p-2 small">
                            {previewLoading && (
                                <span className="text-muted">Загрузка…</span>
                            )}
                            {!previewLoading && !previewDisplay && (
                                <span className="text-muted">Нажать «Показать превью», чтобы загрузить выборку пользователей.</span>
                            )}
                            {!previewLoading && previewDisplay && (
                                <>
                                    {previewDisplay.summary && (
                                        <div className="text-muted mb-2 pb-2 border-bottom">
                                            {previewDisplay.summary}
                                        </div>
                                    )}
                                    {previewDisplay.rows.length === 0 ? (
                                        <span className="text-muted">В этой выборке совпадений нет.</span>
                                    ) : (
                                        <ul className="mb-0 ps-3">
                                            {previewDisplay.rows.map((row, i) => (
                                                <li key={i} className="mb-1">
                                                    <PreviewRow row={row}/>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <div
                    className="border rounded-2 bg-light flex-grow-1 d-flex align-items-center justify-content-center text-muted small text-center px-3"
                    style={{minHeight: 'min(28vh, 240px)'}}
                >
                    Превью по базе доступно только для сохранённого правила.
                </div>
            )}
        </div>
    );
};
